import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useState, useRef, useCallback, useEffect } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import ApiAgent, { handleError, DOMAIN, API_V1 } from '@/common/agent/ApiAgent';
import { useAccessTokenStore } from '@/auth/signin/datacontroller/AccessTokenStore';
import { useRenewAccessToken } from '@/auth/signin/datacontroller/RenewAccessTokenHook';
import {
  CustomerListRes,
  CustomerListReq,
  CustomerDetail,
  CartProduct,
  WishlistProduct,
  CustomerPurchaseHistory,
} from '../model/CustomerTypes';
import type {
  CustomerProfileResponse,
  ProfileProgressEvent,
  CustomerProfileStreamState,
} from '../model/CustomerProfileTypes';
import { parseProfileSSEEvent } from '../util/profileSSEUtils';
import { parseYYYYMMDD } from '../../detail/util/dateUtil';
import { format } from 'date-fns';
import {
  TokenRenewedError,
  MAX_API_RETRY_COUNT,
  MAX_SSE_RECONNECT_COUNT,
  SSE_IDLE_TIMEOUT,
  SSE_ERROR_MESSAGES,
  getReconnectDelay,
} from '@/common/util/sseUtil';
import { useCustomerProfilePrefetchStore } from '../datacontroller/CustomerProfilePrefetchStore';

// Response types for internal use
interface CustomerCartProductsRes {
  items: CartProduct[];
}

interface CustomerWishlistProductsRes {
  items: WishlistProduct[];
}

interface CustomerPurchaseHistoryRes {
  items: CustomerPurchaseHistory[];
}

// QueryKey functions
function getCustomerListQueryKey(params?: CustomerListReq) {
  if (!params) {
    return ['customers'] as const;
  }

  return [
    'customers',
    params.per_page,
    params.current_page,
    params.based_on,
    params.sort_by,
    params.keyword ?? null,
    params.purchase_department_ids ?? null,
  ] as const;
}

function getCustomerDetailQueryKey(customerId: string) {
  return ['customers', customerId] as const;
}

function getCustomerCartProductsQueryKey(customer_id?: string) {
  return ['customers', customer_id, 'cart-products'] as const;
}

function getCustomerWishlistProductsQueryKey(customer_id?: string) {
  return ['customers', customer_id, 'wishlist-products'] as const;
}

function getCustomerPurchaseHistoryQueryKey(customer_id?: string) {
  return ['products', 'purchase-history', customer_id] as const;
}

function getCustomerProfileQueryKey(customer_id?: string, current_date?: string) {
  return ['customer-analysis', 'profile', customer_id, current_date] as const;
}

// useQuery hooks
const useGetCustomerList = (
  params: CustomerListReq,
  options?: Omit<UseQueryOptions<CustomerListRes>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<CustomerListRes>({
    queryKey: getCustomerListQueryKey(params),
    queryFn: async () => {
      try {
        const response = await ApiAgent.get<CustomerListRes>('customers', {
          params,
        });
        return response.data;
      } catch (error) {
        handleError(error);
        throw error;
      }
    },
    ...options,
  });
};

const useGetCustomerDetail = (
  customerId: string,
  options?: Omit<UseQueryOptions<CustomerDetail>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<CustomerDetail>({
    queryKey: getCustomerDetailQueryKey(customerId),
    queryFn: async () => {
      try {
        const response = await ApiAgent.get<CustomerDetail>(`customers/${customerId}`);
        return response.data;
      } catch (error) {
        handleError(error);
        throw error;
      }
    },
    enabled: !!customerId,
    ...options,
  });
};

const useGetCustomerCartProducts = (
  customer_id?: string,
  options?: Omit<UseQueryOptions<CartProduct[]>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<CartProduct[]>({
    queryKey: getCustomerCartProductsQueryKey(customer_id),
    queryFn: async () => {
      try {
        const response = await ApiAgent.get<CustomerCartProductsRes>('/customers/cart-products', {
          params: { cus_cd: customer_id },
        });
        return response.data.items;
      } catch (error) {
        handleError(error);
        throw error;
      }
    },
    enabled: !!customer_id,
    ...options,
  });
};

const useGetCustomerWishlistProducts = (
  customer_id?: string,
  options?: Omit<UseQueryOptions<WishlistProduct[]>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<WishlistProduct[]>({
    queryKey: getCustomerWishlistProductsQueryKey(customer_id),
    queryFn: async () => {
      try {
        const response = await ApiAgent.get<CustomerWishlistProductsRes>('/customers/wishlist-products', {
          params: { cus_cd: customer_id },
        });
        return response.data.items;
      } catch (error) {
        handleError(error);
        throw error;
      }
    },
    enabled: !!customer_id,
    ...options,
  });
};

const useGetCustomerPurchaseHistory = (
  customer_id?: string,
  options?: Omit<UseQueryOptions<CustomerPurchaseHistory[]>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<CustomerPurchaseHistory[]>({
    queryKey: getCustomerPurchaseHistoryQueryKey(customer_id),
    queryFn: async () => {
      try {
        const response = await ApiAgent.get<CustomerPurchaseHistoryRes>(`/products/purchase-history`, {
          params: { cus_cd: customer_id },
        });

        const items = response.data.items;

        if (!items || items.length === 0) {
          return items;
        }

        return items.sort((a, b) => {
          const dateA = parseYYYYMMDD(a.purchase_date);
          const dateB = parseYYYYMMDD(b.purchase_date);

          if (!dateA || !dateB) return 0;

          return dateB.getTime() - dateA.getTime();
        });
      } catch (error) {
        handleError(error);
        throw error;
      }
    },
    enabled: !!customer_id,
    ...options,
  });
};

const useGetCustomerProfile = (
  customer_id?: string,
  options?: Omit<UseQueryOptions<CustomerProfileResponse>, 'queryKey' | 'queryFn'>,
) => {
  const current_date = format(new Date(), 'yyyy-MM-dd');

  return useQuery<CustomerProfileResponse>({
    queryKey: getCustomerProfileQueryKey(customer_id, current_date),
    queryFn: async () => {
      try {
        const response = await ApiAgent.post<CustomerProfileResponse>(
          '/customer-analysis/profile',
          {
            cus_cd: customer_id,
            current_date,
          },
        );
        return response.data;
      } catch (error) {
        handleError(error);
        throw error;
      }
    },
    enabled: !!customer_id,
    staleTime: 1000 * 60 * 5, // 5분 캐시
    ...options,
  });
};

// ============================================================
// SSE 스트리밍 기반 고객 프로필 분석
// ============================================================

/**
 * SSE 스트리밍 기반 고객 프로필 분석 훅
 * - progress: 3단계 진행 상황 (profile_fetcher → similar_customer_analyzer → clerk_tip_generator)
 * - token: LLM 응대 멘트 실시간 토큰
 * - complete: 최종 전체 JSON 응답
 */
const useGetCustomerProfileStream = (customer_id?: string): CustomerProfileStreamState => {
  const [data, setData] = useState<CustomerProfileResponse | null>(null);
  const [progress, setProgress] = useState<ProfileProgressEvent | null>(null);
  const [streamingText, setStreamingText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accessToken = useAccessTokenStore((state) => state.token);
  const { renewAccessToken } = useRenewAccessToken();
  const controllerRef = useRef<AbortController | null>(null);
  const failedApiCallCount = useRef(0);
  const reconnectAttemptRef = useRef(0);
  const isCompleteRef = useRef(false);
  const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // stale closure 방지를 위한 ref
  const accessTokenRef = useRef(accessToken);
  accessTokenRef.current = accessToken;

  /** Idle timeout 정리 */
  const clearIdleTimeout = useCallback(() => {
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }
  }, []);

  /** Idle timeout 리셋 (이벤트 수신 시마다 호출) */
  const resetIdleTimeout = useCallback(
    (controller: AbortController) => {
      clearIdleTimeout();
      idleTimeoutRef.current = setTimeout(() => {
        if (!controller.signal.aborted && !isCompleteRef.current) {
          controller.abort();
          setError(SSE_ERROR_MESSAGES.CONNECTION_TIMEOUT);
          setIsStreaming(false);
          setIsConnecting(false);
        }
      }, SSE_IDLE_TIMEOUT);
    },
    [clearIdleTimeout, setError, setIsStreaming, setIsConnecting],
  );

  const cancelStream = useCallback(() => {
    clearIdleTimeout();
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    setIsStreaming(false);
    setIsConnecting(false);
  }, [clearIdleTimeout]);

  const startStream = useCallback(async () => {
    if (!customer_id) return;

    // 이전 연결 정리
    cancelStream();

    // 상태 초기화
    setData(null);
    setProgress(null);
    setStreamingText('');
    setError(null);
    setIsConnecting(true);
    reconnectAttemptRef.current = 0;
    isCompleteRef.current = false;

    const controller = new AbortController();
    controllerRef.current = controller;

    const current_date = format(new Date(), 'yyyy-MM-dd');

    /** 재연결 시도 공통 로직 */
    const tryReconnect = () => {
      clearIdleTimeout();
      if (reconnectAttemptRef.current < MAX_SSE_RECONNECT_COUNT) {
        reconnectAttemptRef.current += 1;
        const delay = getReconnectDelay(reconnectAttemptRef.current);
        setTimeout(() => {
          if (!controller.signal.aborted) {
            executeRequest();
          }
        }, delay);
      } else {
        setError(SSE_ERROR_MESSAGES.CONNECTION_LOST);
        setIsStreaming(false);
        setIsConnecting(false);
      }
    };

    const executeRequest = async () => {
      try {
        await fetchEventSource(`${DOMAIN}${API_V1}/customer-analysis/profile`, {
          method: 'POST',
          signal: controller.signal,
          openWhenHidden: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessTokenRef.current}`,
          },
          body: JSON.stringify({
            cus_cd: customer_id,
            current_date,
            stream: true,
          }),
          onopen: async (response) => {
            setIsConnecting(false);

            if (response.ok) {
              failedApiCallCount.current = 0;
              setIsStreaming(true);
              // 연결 성공 시 idle timeout 시작
              resetIdleTimeout(controller);
            } else if (response.status === 401) {
              // 토큰 만료 시 갱신 후 재시도
              failedApiCallCount.current += 1;
              if (failedApiCallCount.current < MAX_API_RETRY_COUNT) {
                await renewAccessToken();
                throw new TokenRenewedError();
              } else {
                setError(SSE_ERROR_MESSAGES.AUTH_FAILED);
                throw new Error('Authentication failed');
              }
            } else if (response.status >= 400 && response.status < 500) {
              setError(SSE_ERROR_MESSAGES.CLIENT_ERROR(response.status));
              throw new Error('Client error');
            } else {
              setError(SSE_ERROR_MESSAGES.SERVER_ERROR(response.status));
              throw new Error('Server error');
            }
          },
          onmessage: (event) => {
            // 이벤트 수신 시마다 idle timeout 리셋
            resetIdleTimeout(controller);

            const finalizeStream = () => {
              isCompleteRef.current = true;
              clearIdleTimeout();
              setIsStreaming(false);
            };

            parseProfileSSEEvent(event.data, {
              onProgress: setProgress,
              onToken: (token) => setStreamingText((prev) => prev + token),
              onComplete: (completeData) => {
                finalizeStream();
                setData(completeData);
              },
              onDone: finalizeStream,
              onError: (errorMsg) => {
                finalizeStream();
                setError(errorMsg);
              },
            });
          },
          onclose: () => {
            clearIdleTimeout();
            // 정상 완료 또는 의도적 취소인 경우 재연결하지 않음
            if (isCompleteRef.current || controller.signal.aborted) {
              setIsStreaming(false);
              setIsConnecting(false);
              return;
            }
            tryReconnect();
          },
          onerror: (err) => {
            clearIdleTimeout();
            if (controller.signal.aborted) return;
            tryReconnect();
            throw err; // 라이브러리 자동 재연결 방지
          },
        });
      } catch (err) {
        if (controller.signal.aborted) {
          // 의도적인 취소는 무시
          return;
        }

        // 토큰 갱신 후 재시도
        if (err instanceof TokenRenewedError) {
          executeRequest();
          return;
        }
      } finally {
        setIsConnecting(false);
      }
    };

    executeRequest();
  }, [customer_id, renewAccessToken, cancelStream, resetIdleTimeout, clearIdleTimeout]);

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      cancelStream();
    };
  }, [cancelStream]);

  // customer_id 변경 시 자동 시작 (prefetch 소비 포함)
  useEffect(() => {
    if (!customer_id) return;

    const prefetchState = useCustomerProfilePrefetchStore.getState();

    const applyPrefetchSnapshot = (snapshot: {
      data: CustomerProfileResponse | null;
      streamingText: string;
      progress: ProfileProgressEvent | null;
      error: string | null;
    }) => {
      setData(snapshot.data);
      setStreamingText(snapshot.streamingText);
      setProgress(snapshot.progress);
      setError(snapshot.error);
      setIsStreaming(false);
      setIsConnecting(false);
    };

    // Case 0: 이전에 소비된 동일 고객의 snapshot 재사용
    const { lastConsumedId, lastConsumedSnapshot } = prefetchState;
    if (lastConsumedId === customer_id && lastConsumedSnapshot?.data) {
      applyPrefetchSnapshot(lastConsumedSnapshot);
      return;
    }

    // Case 1: prefetch 완료 데이터 존재
    if (prefetchState.customerId === customer_id && prefetchState.data) {
      applyPrefetchSnapshot(prefetchState);
      prefetchState.cancelAndReset();
      return;
    }

    // Case 2: prefetch 진행 중 - store subscribe로 실시간 전달
    if (prefetchState.customerId === customer_id && (prefetchState.isStreaming || prefetchState.isConnecting)) {
      setStreamingText(prefetchState.streamingText);
      setProgress(prefetchState.progress);
      setIsStreaming(prefetchState.isStreaming);
      setIsConnecting(prefetchState.isConnecting);

      const unsubscribe = useCustomerProfilePrefetchStore.subscribe((state) => {
        if (state.customerId !== customer_id) {
          unsubscribe();
          return;
        }
        setStreamingText(state.streamingText);
        setProgress(state.progress);
        setIsStreaming(state.isStreaming);
        setIsConnecting(state.isConnecting);
        setError(state.error);
        if (state.data) {
          setData(state.data);
          useCustomerProfilePrefetchStore.getState().cancelAndReset();
          unsubscribe();
        }
      });

      return () => unsubscribe();
    }

    // Case 3: prefetch 없음 또는 에러 - 기존 동작
    startStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer_id]);

  return {
    data,
    progress,
    streamingText,
    isStreaming,
    isConnecting,
    error,
    startStream,
    cancelStream,
  };
};

const CustomerApiAgent = {
  useGetCustomerList,
  useGetCustomerDetail,
  useGetCustomerCartProducts,
  useGetCustomerWishlistProducts,
  useGetCustomerPurchaseHistory,
  useGetCustomerProfile,
  useGetCustomerProfileStream,
};

export default CustomerApiAgent;
